import React from 'react';
import type { RegulatoryCheckDisplay } from '../../../../ManagementProfileTab/types';
import { NoDataLabel, NotCheckedLabel } from '../../../../ManagementProfileTab/CheckStatusLabels';
import { DRAWER_ROW_LABELS, INDICATOR_UNITS, LOCKED_COLUMN_TOOLTIPS } from '../../../../ManagementProfileTab/ManagementProfileTabConstants';
import {
    NOT_CHECKED_TOOLTIP,
    REGULATORY_CLICK_TOOLTIP,
    REGULATORY_NO_DATA_TOOLTIP,
    RISK_STATE_CONFIG,
    RegulatoryRiskState,
} from '../../../../../../RiskIndicators/RiskIndicatorConstants';
import DrawerRow from './DrawerRow';
import RegulatoryCountRow from './RegulatoryCountRow';

interface RegulatoryChecksRowProps {
    display: RegulatoryCheckDisplay;
    locked?: boolean;
    notConfigured?: boolean;
    onClickIfPositive?: () => void;
    isPDFReport?: boolean;
}

const LABEL = DRAWER_ROW_LABELS.REGULATORY_CHECKS;

// The count RegulatoryCountRow draws for a resolved state: the badge count, 0 for No Match, else none.
const countFor = (display: RegulatoryCheckDisplay): number | null | undefined => {
    if (display.kind === 'badge') return display.badge.count;
    return display.kind === 'noMatch' ? 0 : null;
};

/**
 * The drawer's Regulatory Checks row, from the same resolved state as the Management Profile cell.
 * Like every drawer row it shows the payload even when the module is locked (see getCountRowStatus).
 */
const RegulatoryChecksRow = ({ display, locked, notConfigured, onClickIfPositive, isPDFReport }: RegulatoryChecksRowProps) => {
    if (!notConfigured && display.kind === 'noData') {
        return <DrawerRow label={LABEL} valueOwnsStyling title={REGULATORY_NO_DATA_TOOLTIP} value={<NoDataLabel />} />;
    }
    if (!notConfigured && display.kind === 'notChecked') {
        return <DrawerRow label={LABEL} valueOwnsStyling title={NOT_CHECKED_TOOLTIP} value={<NotCheckedLabel />} />;
    }
    const badge = display.kind === 'badge' ? display.badge : null;
    return (
        <RegulatoryCountRow
            label={LABEL}
            count={countFor(display)}
            badge={badge}
            locked={locked}
            notConfigured={notConfigured}
            unitSingular={INDICATOR_UNITS.SINGULAR}
            unitPlural={INDICATOR_UNITS.PLURAL}
            zeroLabel={RISK_STATE_CONFIG[RegulatoryRiskState.NO_RISK].label ?? ''}
            onClickIfPositive={onClickIfPositive}
            isPDFReport={isPDFReport}
            lockedTooltip={LOCKED_COLUMN_TOOLTIPS.regulatoryChecks}
            tooltip={badge ? REGULATORY_CLICK_TOOLTIP : undefined}
        />
    );
};

export default RegulatoryChecksRow;
